import { Attributes, DateUtils, List, Misc, Model, ModelBase, Validation } from '@singularsystems/neo-core';
import TemporalEntity from '../TemporalEntity';
import InvestmentRate from './InvestmentRate';
import { InterestRateModifier } from '../Rates/InterestRateModifier.enum';
import CalculationOptions from './CalculationOptions';
import ShareTransactionCommand from '../Transactions/Commands/ShareTransactionCommand';
import ShareTransactionLookup from '../Transactions/Lookups/ShareTransactionLookup';
import DateCycle from '../Dividends/DateCycle';
import { IInvestmentSaveResult } from './Lookups/InvestmentSaveResult';
import DividendAccrualLookup from '../Dividends/Lookups/DividendAccrualLookup';
import DividendLookup from '../Dividends/Lookups/DividendLookup';
import TransactionRequestLookup from '../Transactions/Lookups/TransactionRequestLookup';
import CreateTransactionResult from '../Transactions/Lookups/CreateTransactionResult';
import AccrualAdjustment from './AccrualAdjustment';

export default class Investment extends TemporalEntity {

    constructor() {
        super();
        this.makeObservable();
    }

    public investmentId: number = 0;

    @Attributes.Date(Misc.TimeZoneFormat.None)
    public endDate: Date | null = null;

    @Attributes.Float()
    public valuePerShare: number = 0;

    @Attributes.Serialisation(Misc.SerialiseType.NotNull)
    public dividendCycleId: number | null = null;

    @Attributes.ChildObject(DateCycle)
    public dividendCycle: DateCycle | null = null;

    @Attributes.Nullable()
    public compoundingCycleId: number | null = null;

    @Attributes.ChildObject(DateCycle)
    public compoundingCycle: DateCycle | null = null;

    public completeDividendCycle: boolean = false;

    public endOfMonthReporting: boolean = false;

    public calculationOptions: CalculationOptions = new CalculationOptions();

    public investmentRates = new List(InvestmentRate);

    @Attributes.Date(Misc.TimeZoneFormat.None)
    public initialEndDate: Date | null = null;

    @Attributes.Nullable()
    @Attributes.Float()
    public parValuePerShare: number | null = null;

    @Attributes.Nullable()
    @Attributes.Float()
    public premiumPerShare: number | null = null;

    public reinvestDividends: boolean = false;

    @Attributes.NoTracking(Misc.SerialiseType.FullOnly)
    @Attributes.ChildObject(() => LookupFields)
    public lookup: LookupFields = new LookupFields();

    @Attributes.ChildList(AccrualAdjustment)
    public accrualAdjustments = new List(AccrualAdjustment);

    @Attributes.NoTracking(Misc.SerialiseType.NotNull)
    public initialTransaction: ShareTransactionCommand | null = null;

    // Client only properties / methods

    /** True if the investmentRates list must have a rate populated before or on the start date. */
    public get ratesRequired() {
        return this.calculationOptions.interestRateType === null || this.calculationOptions.interestRateType.modifierType !== InterestRateModifier.None;
    }

    /** True if the investment can be deleted. */
    public get canDelete() {
        return this.lookup.shareTransactions.length === 0;
    }

    public onInitialEndDateChanged() {
        if (this.isNew) {
            this.endDate = this.initialEndDate;
        }
    }

    public afterSave(saveResult: IInvestmentSaveResult) {
        if (this.isNew) {
            this.initialTransaction = null;
            if (saveResult.initialTransaction) {
                const transactionResult = CreateTransactionResult.fromJSObject<CreateTransactionResult>(saveResult.initialTransaction);
                if (transactionResult.shareTransactionLookup) {
                    this.lookup.shareTransactions.push(transactionResult.shareTransactionLookup);
                }
                if (transactionResult.pendingRequest) {
                    this.lookup.pendingTransactionRequests.push(transactionResult.pendingRequest);
                }
            }
        }
        if (saveResult.resetAccruals) {
            this.lookup.resetAccruals();
        }
    }

    public static ratesRequiredRule: Validation.IRule;

    protected addBusinessRules(rules: Validation.Rules<this>) {
        super.addBusinessRules(rules);

        rules.failWhen(c => c.valuePerShare <= 0, "Value per share is required.");
        rules.failWhen(c => !c.dividendCycleId && !c.dividendCycle, "Dividend Cycle is required.");

        rules.add(this.endDateRule).onProperties(c => c.endDate);
        Investment.ratesRequiredRule = rules.add(this.rateRule);

        rules.failWhen(c => c.initialEndDate != null && !c.initialEndDate > !c.endDate, "The end date cannot be earlier than the initial end date.");
    }

    private endDateRule(context: Validation.IRuleContext) {
        const transactions = this.lookup.shareTransactions;

        if (transactions.length > 0) {
            const lastTransaction = transactions[transactions.length - 1];
            if (this.endDate && lastTransaction?.transactionDate && this.endDate < lastTransaction.transactionDate) {
                context.addError(`End date cannot be before ${DateUtils.format(lastTransaction.transactionDate, "dd MMM yyyy")}.`);
            }
        }
    }

    private rateRule(context: Validation.IRuleContext) {
        if (this.investmentRates.activeLength === 0) {
            if (this.ratesRequired) {
                context.addError("At least one investment rate is required.");
            }
        } else if (this.lookup.shareTransactions.length > 0) {
            const firstRate = this.investmentRates[0];
            const firstTransaction = this.lookup.shareTransactions[0];
            if (firstRate?.effectiveDate && firstTransaction?.transactionDate && firstRate.effectiveDate > firstTransaction.transactionDate) {
                context.addError("The first rates effective date cannot be after the first transaction date.");
            }
        }
    }

    /** Clones an existing investment. So that a copy is created on save. */
    public static fromExistingInvestment(investmentData: Model.PlainObject<Investment>): Investment {
        const investment = this.mapFrom<Investment>(investmentData);
        investment.dividendCycle = null;
        investment.investmentId = 0;
        investment.calculationOptions.interestRateType?.markOld();
        return investment;
    }

    public toString(): string {
        if (this.isNew) {
            return "New investment";
        } else {
            return "Investment";
        }
    }

}

export class LookupFields extends ModelBase {

    constructor() {
        super();
        this.makeObservable();
    }

    public dividendCycleName: string = "";

    public compoundingCycleName: string | null = null;

    public maxDividendDate: Date | null = null;

    public shareTransactions = new List(ShareTransactionLookup);

    public pendingTransactionRequests = new List(TransactionRequestLookup);

    @Attributes.Float()
    public investorBalance: number = 0

    public minimumLimit: number = 0

    public minimumTransactionAmount: number = 0;

    // Client only properties / methods

    public get hasDividends() {
        return this.maxDividendDate !== null;
    }

    /** Flat list of accruals from start to end date. */
    @Attributes.NoTracking()
    public accruals: DividendAccrualLookup[] | null = null;

    /** Accruals that are dividends / dividend dates. */
    @Attributes.NoTracking()
    public dividends: DividendLookup[] = [];

    public setAccruals(accruals: DividendAccrualLookup[]) {

        const dividends: DividendLookup[] = [];
        let calculations: DividendAccrualLookup[] = [];

        const last = accruals.length === 0 ? null : accruals[accruals.length - 1];

        for (let accrual of accruals) {
            if (accrual.isDividend || accrual === last) {
                const dividendLookup = new DividendLookup(accrual);
                accrual.dividendLookup = dividendLookup;

                dividends.push(dividendLookup);

                if (accrual.dividend?.ignore) {
                    calculations.push(accrual);
                } else {
                    dividendLookup.calculations = calculations;
                    calculations = [accrual];
                }

            } else {
                calculations.push(accrual);
            }
        }

        this.accruals = accruals;
        this.dividends = dividends;
    }

    public findAccrual(date: Date) {
        return this.accruals?.find(c => c.startDate <= date && (c.endDate > date || c.startDate.getTime() === date.getTime())) ?? null;
    }

    public resetAccruals() {
        this.accruals = null;
        this.dividends = [];
    }
}